/** A labelled section of translation fields (rendered as one form group). */
export interface TranslationGroup {
  /** i18n suffix: the group heading renders t(`translationKeysMeta.groups.${id}`). */
  id: string;
  keys: string[];
}

/**
 * Every translation key the uploader uses, grouped by feature area. Group
 * order and key order determine the form order. Keep this in sync with the
 * uploader's t() usage (wedding-upload/src). Field labels live in the admin
 * i18n catalogs under `translationKeysMeta.<key>`.
 */
export const TRANSLATION_GROUPS: TranslationGroup[] = [
  {
    id: "uploadSelect",
    keys: [
      "upload.select.title",
      "upload.select.subtitle",
    ],
  },
  {
    id: "uploadReview",
    keys: [
      "upload.review.title",
      "upload.review.subtitle",
      "upload.review.countLabel",
      "upload.review.selectedCount",
    ],
  },
  {
    id: "uploadUploading",
    keys: [
      "upload.uploading.title",
      "upload.uploading.subtitle",
      "upload.uploading.progress",
    ],
  },
  {
    id: "uploadConfirmation",
    keys: [
      "upload.confirmation.thanks",
      "upload.confirmation.subtitle",
      "upload.confirmation.successCount",
      "upload.confirmation.failCount",
      "upload.confirmation.cancelCount",
      "upload.confirmation.checkFailedBtn",
      "upload.confirmation.uploadMoreBtn",
    ],
  },
  {
    id: "uploadHeader",
    keys: [
      "upload.header.step",
      "upload.header.selectTitle",
      "upload.header.reviewTitle",
      "upload.header.uploadingTitle",
      "upload.header.confirmationTitle",
      "upload.footer.sendBtn",
      "upload.footer.removeBtn",
      "upload.footer.addMoreBtn",
      "upload.footer.deselectAllBtn",
      "upload.footer.stopBtn",
    ],
  },
  {
    id: "fileUpload",
    keys: [
      "fileUpload.unsupportedFormat",
      "fileUpload.fileTooLarge",
      "fileUpload.clickToOpen",
      "fileUpload.dropzone",
      "fileUpload.selectBtn",
      "fileUpload.dragDrop",
    ],
  },
  {
    id: "gallery",
    keys: [
      "gallery.emptyTitle",
      "gallery.emptySubtitle",
      "gallery.endTitle",
      "gallery.endSubtitle",
      "gallery.loadingMore",
    ],
  },
  {
    id: "download",
    keys: [
      "download.title",
      "download.subtitle",
      "download.downloadBtn",
      "download.createInfo",
      "download.failedLast",
      "download.noneYet",
      "download.fetchError",
    ],
  },
  {
    id: "timeline",
    keys: [
      "timeline.title",
      "timeline.subtitle",
      "timeline.now",
      "timeline.upNext",
      "timeline.empty",
    ],
  },
  {
    id: "foodMenu",
    keys: [
      "foodMenu.title",
      "foodMenu.subtitle",
      "foodMenu.empty",
    ],
  },
  {
    id: "dietary",
    keys: [
      "dietary.vegetarian",
      "dietary.vegan",
      "dietary.glutenFree",
      "dietary.lactoseFree",
      "dietary.nutFree",
      "dietary.spicy",
    ],
  },
  {
    id: "djRequests",
    keys: [
      "dj.title",
      "dj.subtitle",
      "dj.djSubtitle",
      "dj.searchPlaceholder",
      "dj.clearSearch",
      "dj.searchError",
      "dj.noResults",
      "dj.add",
      "dj.added",
      "dj.addError",
      "dj.djSetAside",
      "dj.alreadyPlayedConfirm",
      "dj.readd",
      "dj.cancel",
      "dj.empty",
      "dj.activeEmpty",
      "dj.doneEmpty",
      "dj.rejectedEmpty",
      "dj.listError",
      "dj.vote",
      "dj.top",
      "dj.newest",
      "dj.active",
      "dj.done",
      "dj.declined",
      "dj.confirm",
      "dj.reject",
      "dj.restore",
      "dj.dj",
      "dj.djLogin",
      "dj.djMode",
      "dj.logout",
      "dj.enter",
      "dj.pinPrompt",
      "dj.pinWrong",
    ],
  },
  {
    id: "navigation",
    keys: [
      "menu.upload",
      "menu.gallery",
      "menu.download",
      "menu.timeline",
      "menu.foodMenu",
      "menu.djRequests",
      "menu.label",
    ],
  },
  {
    id: "common",
    keys: [
      "common.fileCount",
      "common.preparingPreview",
    ],
  },
];

/** Flat list of every key in group / form order. */
export const TRANSLATION_KEYS: string[] = TRANSLATION_GROUPS.flatMap(
  (group) => group.keys,
);

export const REQUIRED_TRANSLATION_KEYS = TRANSLATION_KEYS;

export function createEmptyTranslationKeys(): Record<string, string> {
  return Object.fromEntries(TRANSLATION_KEYS.map((key) => [key, ""]));
}

export function buildTranslationDetailPath(variant: string): string {
  return `/translations/${encodeURIComponent(variant)}`;
}

export function buildTranslationEditPath(variant: string): string {
  return `${buildTranslationDetailPath(variant)}/edit`;
}

export function exportTranslationsJson(variant: string, keys: Record<string, string>) {
  const payload = JSON.stringify({ keys }, null, 2);
  const blob = new Blob([payload], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `translations-${variant || "variant"}.json`;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}